/**
 * Types and utilities for building timeline layer hierarchy from jsonFile
 */

import type {
  KeyframePropertyType,
  KeyframeInfo,
  KeyframeRow,
  TextAnimationMetadata,
} from "./keyframeHelpers";

// Full names for property type display
export const PROPERTY_NAMES: Record<KeyframePropertyType, string> = {
  position: "Position",
  scale: "Scale",
  rotation: "Rotation",
  opacity: "Opacity",
  anchor: "Anchor",
  shape: "Shape",
  text: "Text",
  skew: "Skew",
  skewAxis: "Skew Axis",
  other: "Other",
};

// Tree node types for unified rendering
export type TimelineTreeNodeType = "layer" | "property";

export interface TimelineLayerNode {
  type: "layer";
  nodeKey: string;
  depth: number;
  layer: TimelineLayerInfo;
  hasChildren: boolean;
  hasKeyframes: boolean;
  keyframesExpanded: boolean;
}

export interface TimelinePropertyNode {
  type: "property";
  nodeKey: string;
  rowId: string;
  depth: number;
  parentNodeKey: string;
  parentLayer: TimelineLayerInfo;
  propertyType: KeyframePropertyType;
  propertyName: string;
  keyframes: KeyframeInfo[];
  textAnimation?: TextAnimationMetadata;
}

export type TimelineTreeNode = TimelineLayerNode | TimelinePropertyNode;

export interface LayerKeyframeData {
  rows: KeyframeRow[];
  hasKeyframes: boolean;
}

export interface TimelineLayerInfo {
  uid?: number;
  nm: string;
  ty?: number;
  ip: number;
  op: number;
  st?: number;
  parent?: number;
  timelineOffset?: number;
  refId?: string;
  assetId?: string;
  assetName?: string;
  assetPath?: string;
  layerPath: string;
  instancePath: string;
  nodeKey: string;
  children?: TimelineLayerInfo[];
  childCount?: number;
  isPrecomp?: boolean;
}

interface LayerInfo {
  uid?: number;
  nm: string;
  ty?: number;
  ip?: number;
  op?: number;
  st?: number;
  parent?: number;
  refId?: string;
}

interface AssetInfo {
  id: string;
  nm?: string;
  p?: string;
  layers?: LayerInfo[];
}

interface JsonFile {
  layers?: LayerInfo[];
  assets?: AssetInfo[];
  ip?: number;
  op?: number;
  fr?: number;
}

interface BuildContext {
  assetMap: Map<string, AssetInfo>;
  assetIndexMap: Map<string, number>;
  compositionIp: number;
  compositionOp: number;
}

/**
 * Recursively builds timeline layer info from layers
 */
function buildLayerNodes(
  layers: LayerInfo[],
  context: BuildContext,
  parentAssetId: string | undefined,
  parentAssetName: string | undefined,
  parentAssetPath: string | undefined,
  pathPrefix: string,
  instancePathPrefix: string,
  keyPrefix: string,
  timelineOffset: number = 0,
  visitedAssets: Set<string> = new Set()
): TimelineLayerInfo[] {
  const result: TimelineLayerInfo[] = [];

  layers.forEach((layer, index) => {
    const layerPath = `${pathPrefix}[${index}]`;
    const instancePath = `${instancePathPrefix}[${index}]`;
    const nodeKey = `${keyPrefix}-layer-${layer.uid ?? index}`;

    // Use composition ip/op as fallback if layer doesn't have them
    const localIp = layer.ip ?? context.compositionIp;
    const localOp = layer.op ?? context.compositionOp;
    const localSt = layer.st ?? localIp;
    const ip = timelineOffset + localIp;
    const op = timelineOffset + localOp;
    const st = timelineOffset + localSt;

    // Check if this is a precomp
    if (layer.refId) {
      const referencedAsset = context.assetMap.get(layer.refId);

      if (referencedAsset?.layers?.length && !visitedAssets.has(layer.refId)) {
        const branchVisited = new Set(visitedAssets);
        branchVisited.add(layer.refId);

        const assetIndex = context.assetIndexMap.get(referencedAsset.id) ?? 0;
        const childPathPrefix = `assets[${assetIndex}].layers`;
        const childInstancePathPrefix = `${instancePath}::assets[${assetIndex}].layers`;
        const childKeyPrefix = `${keyPrefix}-precomp-${layer.uid ?? index}`;

        const children = buildLayerNodes(
          referencedAsset.layers,
          context,
          referencedAsset.id,
          referencedAsset.nm || referencedAsset.id,
          referencedAsset.p,
          childPathPrefix,
          childInstancePathPrefix,
          childKeyPrefix,
          ip,
          branchVisited
        );

        result.push({
          uid: layer.uid,
          nm: layer.nm,
          ty: layer.ty,
          ip,
          op,
          st,
          parent: layer.parent,
          timelineOffset,
          refId: layer.refId,
          assetId: parentAssetId,
          assetName: parentAssetName,
          assetPath: referencedAsset.p || parentAssetPath,
          layerPath,
          instancePath,
          nodeKey,
          children,
          childCount: referencedAsset.layers.length,
          isPrecomp: true,
        });
        return;
      }
    }

    // Regular layer
    result.push({
      uid: layer.uid,
      nm: layer.nm,
      ty: layer.ty,
      ip,
      op,
      st,
      parent: layer.parent,
      timelineOffset,
      refId: layer.refId,
      assetId: parentAssetId,
      assetName: parentAssetName,
      assetPath: parentAssetPath,
      layerPath,
      instancePath,
      nodeKey,
    });
  });

  return result;
}

export interface TimelineData {
  layers: TimelineLayerInfo[];
  compositionStart: number;
  compositionEnd: number;
  frameRate: number;
  totalFrames: number;
  // Content extent: the min/max frames across ALL layers (may extend beyond composition)
  contentMinFrame: number;
  contentMaxFrame: number;
}

export const DEFAULT_TIMELINE_AUTO_EXPAND_LAYER_LIMIT = 200;

function collectTimelineExpansionStats(layers: TimelineLayerInfo[]): {
  layerCount: number;
  expandableKeys: string[];
} {
  let layerCount = 0;
  const expandableKeys: string[] = [];

  const visit = (layerList: TimelineLayerInfo[]) => {
    layerList.forEach((layer) => {
      layerCount += 1;

      if (layer.children?.length) {
        expandableKeys.push(layer.nodeKey);
        visit(layer.children);
      }
    });
  };

  visit(layers);
  return { layerCount, expandableKeys };
}

export function getDefaultExpandedPrecompKeys(
  layers: TimelineLayerInfo[],
  layerLimit: number = DEFAULT_TIMELINE_AUTO_EXPAND_LAYER_LIMIT
): string[] {
  const { layerCount, expandableKeys } = collectTimelineExpansionStats(layers);
  return layerCount <= layerLimit ? expandableKeys : [];
}

/**
 * Recursively finds the min and max frames across all layers (including nested children)
 */
function getContentExtent(
  layers: TimelineLayerInfo[],
  compositionStart: number,
  compositionEnd: number
): { minFrame: number; maxFrame: number } {
  let minFrame = compositionStart;
  let maxFrame = compositionEnd;

  const traverse = (layerList: TimelineLayerInfo[]) => {
    for (const layer of layerList) {
      if (layer.ip < minFrame) minFrame = layer.ip;
      if (layer.op > maxFrame) maxFrame = layer.op;
      if (layer.children) {
        traverse(layer.children);
      }
    }
  };

  traverse(layers);
  return { minFrame, maxFrame };
}

/**
 * Builds timeline layer tree from jsonFile
 * @param jsonFile - The Lottie JSON file
 */
export function buildTimelineLayerTree(jsonFile: unknown): TimelineData {
  const file = jsonFile as JsonFile | undefined;

  if (!file) {
    return {
      layers: [],
      compositionStart: 0,
      compositionEnd: 0,
      frameRate: 30,
      totalFrames: 0,
      contentMinFrame: 0,
      contentMaxFrame: 0,
    };
  }

  const compositionStart = file.ip ?? 0;
  const compositionEnd = file.op ?? 0;
  const frameRate = file.fr ?? 30;

  // Build asset maps
  const assetMap = new Map<string, AssetInfo>();
  const assetIndexMap = new Map<string, number>();

  file.assets?.forEach((asset, index) => {
    assetMap.set(asset.id, asset);
    assetIndexMap.set(asset.id, index);
  });

  const context: BuildContext = {
    assetMap,
    assetIndexMap,
    compositionIp: compositionStart,
    compositionOp: compositionEnd,
  };

  const layers: TimelineLayerInfo[] = [];

  // Build main composition layers
  if (file.layers?.length) {
    const mainLayers = buildLayerNodes(
      file.layers,
      context,
      undefined,
      undefined,
      undefined,
      "layers",
      "layers",
      "main"
    );
    layers.push(...mainLayers);
  }

  // Calculate content extent (includes layers that may extend beyond composition)
  const { minFrame, maxFrame } = getContentExtent(
    layers,
    compositionStart,
    compositionEnd
  );

  return {
    layers,
    compositionStart,
    compositionEnd,
    frameRate,
    totalFrames: compositionEnd - compositionStart,
    contentMinFrame: minFrame,
    contentMaxFrame: maxFrame,
  };
}

/**
 * Get the raw layer data from jsonFile using the layer path.
 * Parses paths like "layers[0]" or "assets[0].layers[2]".
 */
export function getLayerData(jsonFile: unknown, layerPath: string): unknown {
  if (!jsonFile || !layerPath) return null;

  try {
    // Parse path like "layers[0]" or "assets[0].layers[2]"
    const parts = layerPath.match(/([a-zA-Z]+)|\[(\d+)\]/g);
    if (!parts) return null;

    let current: unknown = jsonFile;
    for (const part of parts) {
      if (!current || typeof current !== "object") return null;

      if (part.startsWith("[") && part.endsWith("]")) {
        // Array index
        const index = parseInt(part.slice(1, -1), 10);
        if (Array.isArray(current)) {
          current = current[index];
        } else {
          return null;
        }
      } else {
        // Object property
        current = (current as Record<string, unknown>)[part];
      }
    }

    return current;
  } catch {
    // Return null on any parsing error to prevent render crashes
    return null;
  }
}

/**
 * Flattens the layer tree for rendering, respecting expanded state
 */
export function flattenLayers(
  layers: TimelineLayerInfo[],
  expandedKeys: Set<string>,
  depth: number = 0
): Array<{ layer: TimelineLayerInfo; depth: number }> {
  const result: Array<{ layer: TimelineLayerInfo; depth: number }> = [];

  layers.forEach((layer) => {
    result.push({ layer, depth });

    if (
      layer.children &&
      layer.children.length > 0 &&
      expandedKeys.has(layer.nodeKey)
    ) {
      const children = flattenLayers(layer.children, expandedKeys, depth + 1);
      result.push(...children);
    }
  });

  return result;
}

/**
 * Flattens the layer tree into unified tree nodes (layers and properties)
 * Both layers and properties are returned as separate nodes for consistent tree rendering
 */
export function flattenTimelineTree(
  layers: TimelineLayerInfo[],
  expandedPrecompKeys: Set<string>,
  layerKeyframeData: Map<string, LayerKeyframeData>,
  isLayerKeyframesExpanded: (nodeKey: string) => boolean,
  depth: number = 0
): TimelineTreeNode[] {
  const result: TimelineTreeNode[] = [];

  for (const layer of layers) {
    const kfData = layerKeyframeData.get(layer.nodeKey);
    const keyframesExpanded = isLayerKeyframesExpanded(layer.nodeKey);
    const hasChildren = (layer.children?.length ?? 0) > 0;
    const hasKeyframes = kfData?.hasKeyframes ?? false;

    // Add layer node
    result.push({
      type: "layer",
      nodeKey: layer.nodeKey,
      depth,
      layer,
      hasChildren,
      hasKeyframes,
      keyframesExpanded,
    });

    // Add property nodes if keyframes are expanded and there are keyframes
    if (keyframesExpanded && kfData && kfData.rows.length > 0) {
      for (const row of kfData.rows) {
        result.push({
          type: "property",
          nodeKey: `${layer.nodeKey}-prop-${row.id}`,
          rowId: row.id,
          depth: depth + 1,
          parentNodeKey: layer.nodeKey,
          parentLayer: layer,
          propertyType: row.propertyType,
          propertyName: row.propertyName,
          keyframes: row.keyframes,
          textAnimation: row.textAnimation,
        });
      }
    }

    // Recurse into children if precomp is expanded
    if (hasChildren && expandedPrecompKeys.has(layer.nodeKey)) {
      const childNodes = flattenTimelineTree(
        layer.children!,
        expandedPrecompKeys,
        layerKeyframeData,
        isLayerKeyframesExpanded,
        depth + 1
      );
      result.push(...childNodes);
    }
  }

  return result;
}
